import React, { useState, useMemo } from "react";
import { useGetAllUsers } from "../../../../../api/addEmployee/employee";
import {
  useGroupMembers,
  useRenameGroup,
  useAddGroupMember,
  useRemoveGroupMember,
  useDeleteGroup,
  useMakeGroupAdmin,
  useRemoveGroupAdmin,
} from "../../../../../api/Chats/chats";

import {
  Users,
  X,
  Edit3,
  UserPlus,
  UserMinus,
  Check,
  MoreVertical,
  Crown,
  Search,
  ArrowLeft,
  Trash2,
  Shield,
  ShieldOff,
  AlertTriangle,
} from "lucide-react";

export default function ChatInfo({ chatId, onClose, currentUserId }) {
  const { data, isLoading, isError } = useGroupMembers(chatId);

  const [isEditingName, setIsEditingName] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRemoveMemberConfirm, setShowRemoveMemberConfirm] = useState(false);
  const [showRemoveAdminConfirm, setShowRemoveAdminConfirm] = useState(false);
  const [selectedMemberMenu, setSelectedMemberMenu] = useState(null);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [adminToRemove, setAdminToRemove] = useState(null);

  const BASE_DOWNLOAD_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

  // ✅ NEW: Decode token to check if user is real admin
  const isRealAdmin = useMemo(() => {
    try {
      const decoded = JSON.parse(atob(localStorage.token?.split(".")[1] || ""));
      return decoded.role === "admin";
    } catch {
      return false;
    }
  }, []);

  const { data: usersData, isLoading: usersLoading } =
    useGetAllUsers(searchQuery);
  const renameMutation = useRenameGroup();
  const addMemberMutation = useAddGroupMember();
  const removeMemberMutation = useRemoveGroupMember();
  const deleteGroupMutation = useDeleteGroup();
  const makeAdminMutation = useMakeGroupAdmin();
  const removeAdminMutation = useRemoveGroupAdmin();

  // ✅ Filter out inactive users
  const activeUsers = useMemo(() => {
    if (!usersData) return [];
    return usersData.filter((user) => user.isActive !== false);
  }, [usersData]);

  if (isLoading) {
    return (
      <div className="absolute top-0 right-0 w-full h-full bg-white border-l shadow-lg z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-2"></div>
          <p className="text-gray-500">Loading chat info...</p>
        </div>
      </div>
    );
  }

  if (isError || !data?.chat) {
    return (
      <div className="absolute top-0 right-0 w-full h-full bg-white border-l shadow-lg z-50 flex items-center justify-center">
        <div className="text-center text-red-500">
          <X size={48} className="mx-auto mb-2" />
          <p>Failed to load chat info.</p>
        </div>
      </div>
    );
  }

  const { chat } = data;
  const { chatName, isGroupChat, groupAdmin, groupAdmins = [], users = [] } = chat;
  
  // ✅ Check if current user is any admin (not just creator)
  const isCurrentUserAdmin = groupAdmins.some(
    admin => admin._id === currentUserId
  );
  
  // ✅ Check if user is the original creator
  const isCreator = groupAdmin?._id === currentUserId;

  const otherUser = !isGroupChat
    ? users.find((u) => u._id !== currentUserId)
    : null;

  const getProfilePicUrl = (user) => {
    if (user?.profilePic) {
      return user.profilePic.startsWith("http")
        ? user.profilePic
        : `${BASE_DOWNLOAD_URL}${user.profilePic}`;
    }
    return null;
  };

  const profilePicUrl = getProfilePicUrl(otherUser);

  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const handleRename = () => {
    if (newGroupName.trim() && newGroupName !== chatName) {
      renameMutation.mutate(
        { chatId, name: newGroupName.trim() },
        {
          onSuccess: () => {
            setIsEditingName(false);
            setNewGroupName("");
          },
        }
      );
    } else {
      setIsEditingName(false);
      setNewGroupName("");
    }
  };

  const handleRemoveMember = (userId) => {
    setMemberToRemove(userId);
    setShowRemoveMemberConfirm(true);
  };

  const confirmRemoveMember = () => {
    if (memberToRemove) {
      removeMemberMutation.mutate(
        { chatId, userId: memberToRemove },
        {
          onSuccess: () => {
            setShowRemoveMemberConfirm(false);
            setMemberToRemove(null);
            setSelectedMemberMenu(null);
          },
        }
      );
    }
  };

  const handleAddMember = (userId) => {
    addMemberMutation.mutate(
      { chatId, userId },
      {
        onSuccess: () => {
          setShowAddMember(false);
          setSearchQuery("");
        },
      }
    );
  };

  const startEditingName = () => {
    setNewGroupName(chatName || "");
    setIsEditingName(true);
  };

  const handleMakeAdmin = (userId) => {
    makeAdminMutation.mutate(
      { chatId, userId },
      {
        onSuccess: () => {
          setSelectedMemberMenu(null);
        },
      }
    );
  };

  const handleRemoveAdmin = (userId) => {
    setAdminToRemove(userId);
    setShowRemoveAdminConfirm(true);
  };

  const confirmRemoveAdmin = () => {
    if (adminToRemove) {
      removeAdminMutation.mutate(
        { chatId, userId: adminToRemove },
        {
          onSuccess: () => {
            setShowRemoveAdminConfirm(false);
            setAdminToRemove(null);
            setSelectedMemberMenu(null);
          },
        }
      );
    }
  };

  const handleDeleteGroup = () => {
    deleteGroupMutation.mutate(chatId, {
      onSuccess: () => {
        setShowDeleteConfirm(false);
        onClose();
      },
      onError: (error) => {
        console.error("Failed to delete group:", error);
        alert(error.response?.data?.message || "Failed to delete group");
      },
    });
  };

  const isMemberAdmin = (memberId) => {
    return groupAdmins.some(admin => admin._id === memberId);
  };

  const getMemberName = (userId) => {
    const member = users.find(u => u._id === userId);
    if (!member) return "this member";
    return `${member.firstName || ""} ${member.lastName || ""}`.trim() || member.username || "this member";
  };

  if (showAddMember) {
    return (
      <div className="absolute top-0 right-0 w-full h-full bg-white border-l shadow-lg z-50 flex flex-col">
        {/* Add Member Header */}
        <div className="flex items-center p-3 bg-blue-400 text-white">
          <button
            onClick={() => setShowAddMember(false)}
            className="mr-4 p-1 hover:bg-blue-600 rounded-full transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-lg font-semibold">Add Member</h2>
        </div>

        {/* Search */}
        <div className="p-3 border-b bg-gray-50">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search active users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border text-sm rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        {/* Available Users List - Only Active Users */}
        <div className="flex-1 overflow-y-auto">
          {usersLoading ? (
            <div className="p-6 text-center text-gray-500">
              Loading users...
            </div>
          ) : activeUsers.length > 0 ? (
            activeUsers.map((user) => {
              const alreadyMember = users.some((u) => u._id === user._id);

              return (
                <div
                  key={user._id}
                  className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center text-white font-semibold mr-3 overflow-hidden flex-shrink-0">
                    {getProfilePicUrl(user) ? (
                      <img
                        src={getProfilePicUrl(user)}
                        alt={user.username || "User"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getInitials(
                        `${user.firstName || ""} ${user.lastName || ""}`
                      )
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {`${user.firstName} ${user.lastName}`}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {user.position || user.department || "No info"}
                    </p>
                  </div>

                  {/* Add Button or Already Member Badge */}
                  {alreadyMember ? (
                    <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-medium">
                      Member
                    </span>
                  ) : (
                    <button
                      onClick={() => handleAddMember(user._id)}
                      disabled={addMemberMutation.isLoading}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-full disabled:opacity-50 transition-colors"
                      title="Add to group"
                    >
                      <UserPlus size={18} />
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="p-8 text-center text-gray-500">
              <div className="mb-3">
                <Users size={48} className="mx-auto text-gray-300" />
              </div>
              <p className="font-medium">No active users found</p>
              <p className="text-sm text-gray-400 mt-1">
                {searchQuery
                  ? "Try a different search term"
                  : "All users are either inactive or already members"}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="absolute top-0 right-0 w-full h-full bg-white border-l shadow-lg z-20 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-blue-400 text-white shadow-sm">
        <h1 className="text-lg font-semibold">
          {isGroupChat ? "Group Info" : "Contact Info"}
        </h1>
        <button
          onClick={onClose}
          className="p-1 hover:bg-blue-600 rounded-full transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Profile Section */}
        <div className="flex flex-col items-center p-6 bg-gradient-to-b from-green-50 to-white">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-4xl font-bold shadow-lg border-4 border-white overflow-hidden">
            {isGroupChat ? (
              <Users size={48} />
            ) : profilePicUrl ? (
              <img
                src={profilePicUrl}
                alt={otherUser?.username || "User"}
                className="w-full h-full object-cover"
              />
            ) : (
              getInitials(
                `${otherUser?.firstName || ""} ${otherUser?.lastName || ""}`
              ) || "U"
            )}
          </div>

          {/* Group Name with Edit */}
          <div className="mt-4 w-full max-w-sm">
            {isGroupChat ? (
              isEditingName ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className="flex-1 text-xl font-semibold text-center bg-white border-2 border-blue-300 rounded-lg px-3 py-2 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename();
                      if (e.key === "Escape") {
                        setIsEditingName(false);
                        setNewGroupName("");
                      }
                    }}
                    autoFocus
                    disabled={renameMutation.isLoading}
                  />
                  <button
                    onClick={handleRename}
                    disabled={renameMutation.isLoading}
                    className="p-2 text-blue-600 hover:bg-green-100 rounded-full transition-colors disabled:opacity-50"
                  >
                    {renameMutation.isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                    ) : (
                      <Check size={20} />
                    )}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <h2 className="text-xl font-semibold text-gray-800 text-center">
                    {chatName || "Group Chat"}
                  </h2>
                  {isCurrentUserAdmin && (
                    <button
                      onClick={startEditingName}
                      className="p-1 text-gray-500 hover:bg-gray-200 rounded-full transition-colors"
                    >
                      <Edit3 size={16} />
                    </button>
                  )}
                </div>
              )
            ) : (
              <h2 className="text-xl font-semibold text-gray-800 text-center">
                {`${otherUser?.firstName || ""} ${otherUser?.lastName || ""}`}
              </h2>
            )}
          </div>

          <p className="text-sm text-gray-600 mt-2 text-center">
            {isGroupChat ? (
              <span className="flex items-center justify-center space-x-1">
                <Users size={16} />
                <span>
                  {users.length} member{users.length !== 1 ? "s" : ""}
                  {groupAdmins.length > 0 && (
                    <span className="text-yellow-600 ml-2">
                      • {groupAdmins.length} admin{groupAdmins.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </span>
              </span>
            ) : otherUser?.isOnline ? (
              <span className="text-green-600 font-medium flex items-center justify-center space-x-1">
                <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                <span>Online</span>
              </span>
            ) : (
              <span className="text-gray-500">
                Last seen{" "}
                {new Date(
                  otherUser?.lastSeen || otherUser?.updatedAt
                ).toLocaleString()}
              </span>
            )}
          </p>
        </div>

        {/* Group Actions */}
        {isGroupChat && isCurrentUserAdmin && (
          <div className="bg-white border-b border-gray-100">
            <button
              onClick={() => setShowAddMember(true)}
              disabled={addMemberMutation.isLoading}
              className="w-full flex items-center px-4 py-4 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                {addMemberMutation.isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
                ) : (
                  <UserPlus size={20} className="text-blue-600" />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="text-blue-600 font-medium">Add Member</p>
                <p className="text-xs text-gray-500">
                  Invite active users to join the group
                </p>
              </div>
            </button>

            {/* ✅ UPDATED: Delete Group Button - Only for Real Admins */}
            {isRealAdmin && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteGroupMutation.isLoading}
                className="w-full flex items-center px-4 py-4 hover:bg-red-50 transition-colors disabled:opacity-50 border-t border-gray-100"
              >
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                  {deleteGroupMutation.isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                  ) : (
                    <Trash2 size={20} className="text-red-600" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-red-600 font-medium">Delete Group</p>
                  <p className="text-xs text-gray-500">
                    Permanently delete this group and all messages
                  </p>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Group Members List */}
        {isGroupChat && (
          <div className="bg-white">
            <div className="px-4 py-3 bg-gray-100 border-b">
              <h3 className="font-semibold text-gray-700 flex items-center">
                <Users size={16} className="mr-2" />
                {users.length} member{users.length !== 1 ? "s" : ""}
              </h3>
            </div>

            {users.map((member) => {
              const isCurrentUser = member._id === currentUserId;
              const isMemberGroupAdmin = isMemberAdmin(member._id);
              const isOriginalCreator = member._id === groupAdmin?._id;
              const showMenu = selectedMemberMenu === member._id;

              return (
                <div
                  key={member._id}
                  className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors group relative"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center text-white font-semibold mr-3 flex-shrink-0 overflow-hidden">
                    {getProfilePicUrl(member) ? (
                      <img
                        src={getProfilePicUrl(member)}
                        alt={member.username || "User"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getInitials(
                        `${member.firstName || ""} ${member.lastName || ""}`
                      ) || "U"
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="font-medium text-gray-900 truncate">
                        {`${member.firstName || ""} ${member.lastName || ""}`}
                        {isCurrentUser && (
                          <span className="text-green-600 text-sm ml-2 font-normal">
                            (You)
                          </span>
                        )}
                      </p>
                      {isMemberGroupAdmin && (
                        <Crown
                          size={14}
                          className="text-yellow-500 flex-shrink-0"
                          title="Group Admin"
                        />
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {member.position ||
                        member.department ||
                        "Hey there! I am using ChatApp"}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {isMemberGroupAdmin ? (
                      <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                        Admin
                      </span>
                    ) : member.isOnline ? (
                      <div className="flex items-center space-x-1">
                        <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                        <span className="text-xs text-blue-600 hidden sm:inline">
                          Online
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 hidden sm:inline">
                        {new Date(
                          member.lastSeen || member.updatedAt
                        ).toLocaleDateString()}
                      </span>
                    )}

                    {/* Admin Actions Menu */}
                    {isCurrentUserAdmin && !isCurrentUser && (
                      <div className="relative">
                        <button
                          onClick={() => setSelectedMemberMenu(showMenu ? null : member._id)}
                          className="p-2 text-gray-500 hover:bg-gray-200 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                          title="More options"
                        >
                          <MoreVertical size={16} />
                        </button>

                        {/* Dropdown Menu */}
                        {showMenu && (
                          <>
                            {/* Backdrop to close menu */}
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setSelectedMemberMenu(null)}
                            />
                            
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[200px] overflow-hidden">
                              {/* Make Admin / Remove Admin */}
                              {!isMemberGroupAdmin ? (
                                <button
                                  onClick={() => handleMakeAdmin(member._id)}
                                  disabled={makeAdminMutation.isLoading}
                                  className="w-full flex items-center px-4 py-3 hover:bg-blue-50 transition-colors text-left disabled:opacity-50"
                                >
                                  <Shield size={16} className="text-blue-600 mr-3" />
                                  <span className="text-sm text-gray-700">
                                    Make Group Admin
                                  </span>
                                </button>
                              ) : (
                                !isOriginalCreator && (
                                  <button
                                    onClick={() => handleRemoveAdmin(member._id)}
                                    disabled={removeAdminMutation.isLoading}
                                    className="w-full flex items-center px-4 py-3 hover:bg-orange-50 transition-colors text-left disabled:opacity-50"
                                  >
                                    <ShieldOff size={16} className="text-orange-600 mr-3" />
                                    <span className="text-sm text-gray-700">
                                      Remove Admin
                                    </span>
                                  </button>
                                )
                              )}

                              {/* Remove from Group */}
                              {!isMemberGroupAdmin && (
                                <button
                                  onClick={() => handleRemoveMember(member._id)}
                                  disabled={removeMemberMutation.isLoading}
                                  className="w-full flex items-center px-4 py-3 hover:bg-red-50 transition-colors text-left disabled:opacity-50 border-t border-gray-100"
                                >
                                  <UserMinus size={16} className="text-red-600 mr-3" />
                                  <span className="text-sm text-gray-700">
                                    Remove from Group
                                  </span>
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* One-to-one User Info */}
        {!isGroupChat && otherUser && (
          <div className="bg-white">
            <div className="px-4 py-3 bg-gray-100 border-b">
              <h3 className="font-semibold text-gray-700">Contact Details</h3>
            </div>

            <div className="p-3 space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mt-1 flex-shrink-0">
                  <span className="text-blue-600 text-sm font-semibold">@</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500 mb-1">Username</p>
                  <p className="font-medium text-gray-900 break-words">
                    {otherUser.username || "Not available"}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mt-1 flex-shrink-0">
                  <span className="text-green-600 text-sm">✉</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="font-medium text-gray-900 break-words">
                    {otherUser.email || "Not available"}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mt-1 flex-shrink-0">
                  <span className="text-purple-600 text-sm">🏢</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500 mb-1">Department</p>
                  <p className="font-medium text-gray-900 break-words">
                    {otherUser.department || "Not specified"}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mt-1 flex-shrink-0">
                  <span className="text-orange-600 text-sm">💼</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500 mb-1">Position</p>
                  <p className="font-medium text-gray-900 break-words">
                    {otherUser.position || "Hey there! I am using ChatApp"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Group Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mx-auto mb-4">
              <Trash2 size={32} className="text-red-600" />
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
              Delete Group?
            </h3>
            
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to delete "{chatName}"? This action cannot be undone. All messages and media will be permanently deleted.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteGroupMutation.isLoading}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteGroup}
                disabled={deleteGroupMutation.isLoading}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {deleteGroupMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  "Delete Group"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation Modal */}
      {showRemoveMemberConfirm && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 mx-auto mb-4">
              <UserMinus size={32} className="text-orange-600" />
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
              Remove Member?
            </h3>
            
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to remove {getMemberName(memberToRemove)} from "{chatName}"? They will no longer have access to this group.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRemoveMemberConfirm(false);
                  setMemberToRemove(null);
                }}
                disabled={removeMemberMutation.isLoading}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveMember}
                disabled={removeMemberMutation.isLoading}
                className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {removeMemberMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Removing...
                  </>
                ) : (
                  "Remove Member"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Admin Confirmation Modal */}
      {showRemoveAdminConfirm && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 mx-auto mb-4">
              <AlertTriangle size={32} className="text-yellow-600" />
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
              Remove Admin Privileges?
            </h3>
            
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to remove admin privileges from {getMemberName(adminToRemove)}? They will lose all admin permissions for this group.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowRemoveAdminConfirm(false);
                  setAdminToRemove(null);
                }}
                disabled={removeAdminMutation.isLoading}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveAdmin}
                disabled={removeAdminMutation.isLoading}
                className="flex-1 px-4 py-2.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {removeAdminMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Removing...
                  </>
                ) : (
                  "Remove Admin"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}